import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Offcanvas, OffcanvasBody, Spinner } from 'reactstrap'
import { toast } from 'react-toastify'
import Swal from 'sweetalert2'
import SimpleBar from 'simplebar-react'

import CloseButton from 'Components/Common/CloseButton'
import {
    createCreatorTag,
    deleteCreatorTag,
    fetchCreatorTags,
    updateCreatorTag,
} from 'services/api/creator-tag'
import { fetchContactsThunk } from 'slices/creators/thunk'
import { resetCreatorsState } from 'slices/creators/reducer'
import { AppDispatch, RootState } from 'store'
import { CreatorTag } from 'types/creators'
import { getErrorMessage } from 'utils/helpers'
import './ManageCreatorTagsOffcanvas.scss'

interface ManageCreatorTagsOffcanvasProps {
    isOpen: boolean
    onClose: () => void
}

const normalizeTagName = (name: string) => name.trim()

const ManageCreatorTagsOffcanvas: React.FC<ManageCreatorTagsOffcanvasProps> = ({
    isOpen,
    onClose,
}) => {
    const dispatch = useDispatch<AppDispatch>()
    const agencyId = useSelector((state: RootState) => state.auth.selectedAgencyId)
    const filters = useSelector((state: RootState) => state.creators.filters)

    const [tags, setTags] = useState<CreatorTag[]>([])
    const [loading, setLoading] = useState(false)
    const [newTagName, setNewTagName] = useState('')
    const [creating, setCreating] = useState(false)
    const [editingTagId, setEditingTagId] = useState<number | null>(null)
    const [editingName, setEditingName] = useState('')
    const [savingTagId, setSavingTagId] = useState<number | null>(null)
    const [deletingTagId, setDeletingTagId] = useState<number | null>(null)

    const refreshCreatorsList = useCallback(() => {
        dispatch(resetCreatorsState())
        dispatch(fetchContactsThunk({ force: true, filters: { ...filters, page: 1 } }))
    }, [dispatch, filters])

    const loadTags = useCallback(async () => {
        if (!agencyId) return
        setLoading(true)
        try {
            const data = await fetchCreatorTags(agencyId)
            setTags(data)
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to load tags.'))
        } finally {
            setLoading(false)
        }
    }, [agencyId])

    useEffect(() => {
        if (isOpen) {
            setNewTagName('')
            setEditingTagId(null)
            loadTags()
        }
    }, [isOpen, loadTags])

    const isDuplicateName = (name: string, excludeTagId?: number) => {
        const normalized = normalizeTagName(name).toLowerCase()
        return tags.some(
            (tag) =>
                tag.id !== excludeTagId &&
                normalizeTagName(tag.tag).toLowerCase() === normalized
        )
    }

    const handleCreate = async () => {
        const name = normalizeTagName(newTagName)
        if (!agencyId || !name) return
        if (isDuplicateName(name)) {
            toast.error('A tag with this name already exists.')
            return
        }
        try {
            setCreating(true)
            await createCreatorTag(agencyId, name)
            loadTags()
            setNewTagName('')
            refreshCreatorsList()
            toast.success('Tag created successfully.')
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to create tag.'))
        } finally {
            setCreating(false)
        }
    }

    const startEditing = (tag: CreatorTag) => {
        setEditingTagId(tag.id)
        setEditingName(tag.tag)
    }

    const cancelEditing = () => {
        setEditingTagId(null)
        setEditingName('')
    }

    const handleUpdate = async (tag: CreatorTag) => {
        const name = normalizeTagName(editingName)
        if (!agencyId || !name) return
        if (name === tag.tag) {
            cancelEditing()
            return
        }
        if (isDuplicateName(name, tag.id)) {
            toast.error('A tag with this name already exists.')
            return
        }
        try {
            setSavingTagId(tag.id)
            await updateCreatorTag(agencyId, tag.id, name)
            loadTags()
            cancelEditing()
            refreshCreatorsList()
            toast.success('Tag updated successfully.')
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to update tag.'))
        } finally {
            setSavingTagId(null)
        }
    }

    const handleDelete = async (tag: CreatorTag) => {
        if (!agencyId) return

        const result = await Swal.fire({
            title: `Delete "${tag.tag}"?`,
            text: 'Creators using this tag will no longer have it assigned.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
        })

        if (!result.isConfirmed) return

        try {
            setDeletingTagId(tag.id)
            await deleteCreatorTag(agencyId, tag.id)
            setTags((prev) => prev.filter((item) => item.id !== tag.id))
            if (editingTagId === tag.id) cancelEditing()
            refreshCreatorsList()
            toast.success('Tag deleted successfully.')
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to delete tag.'))
        } finally {
            setDeletingTagId(null)
        }
    }

    return (
        <Offcanvas
            isOpen={isOpen}
            toggle={onClose}
            direction="end"
            className="manage-creator-tags-offcanvas-wrap"
        >
            <CloseButton onClose={onClose} />
            <OffcanvasBody className="p-0 position-relative">
                <div className="payments-offcanvas-bg" />
                <div className="manage-creator-tags-offcanvas">
                    <div className="manage-creator-tags-offcanvas__header">
                        <h2>Manage Tags</h2>
                        <p>Create, rename, or remove tags used to organize your creators.</p>
                    </div>

                    <div className="manage-creator-tags-offcanvas__create">
                        <label htmlFor="new-creator-tag" className="form-label">
                            New tag
                        </label>
                        <div className="manage-creator-tags-offcanvas__create-row">
                            <input
                                id="new-creator-tag"
                                type="text"
                                className="form-control"
                                placeholder="Enter tag name"
                                value={newTagName}
                                onChange={(e) => setNewTagName(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleCreate()
                                }}
                                disabled={creating || !agencyId}
                            />
                            <button
                                type="button"
                                className="theme-btn theme-btn-md btn-gradient"
                                onClick={handleCreate}
                                disabled={creating || !normalizeTagName(newTagName) || !agencyId}
                            >
                                {creating ? 'Adding...' : 'Add'}
                            </button>
                        </div>
                    </div>

                    <div className="manage-creator-tags-offcanvas__section">
                        <div className="manage-creator-tags-offcanvas__section-title">
                            All tags
                            {!loading && <span className="count-badge">{tags.length}</span>}
                        </div>
                        <SimpleBar className="manage-creator-tags-offcanvas__scroll">
                            {loading ? (
                                <div className="d-flex justify-content-center py-5">
                                    <Spinner size="sm" color="primary" />
                                </div>
                            ) : tags.length === 0 ? (
                                <div className="manage-creator-tags-offcanvas__empty">
                                    No tags yet. Add your first tag above.
                                </div>
                            ) : (
                                <ul className="manage-creator-tags-offcanvas__list list-unstyled mb-0">
                                    {tags.map((tag) => {
                                        const isEditing = editingTagId === tag.id
                                        const isSaving = savingTagId === tag.id
                                        const isDeleting = deletingTagId === tag.id

                                        return (
                                            <li
                                                key={tag.id}
                                                className={`manage-creator-tags-offcanvas__item${
                                                    isEditing ? ' is-editing' : ''
                                                }`}
                                            >
                                                {isEditing ? (
                                                    <>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={editingName}
                                                            onChange={(e) => setEditingName(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') handleUpdate(tag)
                                                                if (e.key === 'Escape') cancelEditing()
                                                            }}
                                                            autoFocus
                                                            disabled={isSaving}
                                                        />
                                                        <button
                                                            type="button"
                                                            className="manage-creator-tags-offcanvas__icon-btn manage-creator-tags-offcanvas__icon-btn--success"
                                                            onClick={() => handleUpdate(tag)}
                                                            disabled={
                                                                isSaving ||
                                                                !normalizeTagName(editingName) ||
                                                                normalizeTagName(editingName) === tag.tag
                                                            }
                                                            aria-label="Save tag"
                                                        >
                                                            <i className="ri-check-line" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="manage-creator-tags-offcanvas__icon-btn"
                                                            onClick={cancelEditing}
                                                            disabled={isSaving}
                                                            aria-label="Cancel edit"
                                                        >
                                                            <i className="ri-close-line" />
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="manage-creator-tags-offcanvas__name">
                                                            {tag.tag}
                                                        </span>
                                                        <button
                                                            type="button"
                                                            className="manage-creator-tags-offcanvas__icon-btn"
                                                            onClick={() => startEditing(tag)}
                                                            disabled={isDeleting || !!editingTagId}
                                                            aria-label={`Edit ${tag.tag}`}
                                                        >
                                                            <i className="ri-pencil-line" />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="manage-creator-tags-offcanvas__icon-btn manage-creator-tags-offcanvas__icon-btn--danger"
                                                            onClick={() => handleDelete(tag)}
                                                            disabled={isDeleting || !!editingTagId}
                                                            aria-label={`Delete ${tag.tag}`}
                                                        >
                                                            <i className="ri-delete-bin-line" />
                                                        </button>
                                                    </>
                                                )}
                                            </li>
                                        )
                                    })}
                                </ul>
                            )}
                        </SimpleBar>
                    </div>
                </div>
                <div className="filters-offcanvas-footer d-flex justify-content-end align-items-center px-4 py-3">
                    <button type="button" className="theme-btn theme-btn-md btn-gradient" onClick={onClose}>
                        Done
                    </button>
                </div>
            </OffcanvasBody>
        </Offcanvas>
    )
}

export default ManageCreatorTagsOffcanvas
